// User API service for frontend
// Handles all user-related API calls with proper validation

import apiClient, { ApiResponse } from './apiService';

// Role constants for consistent role management
export const ROLE_GRC_ADMIN = 1;
export const ROLE_ORG_MEMBER = 2;
export const ROLE_AUDIT_USER = 3;
export const ROLE_RISK_ANALYST = 4;
export const ROLE_C_SUITE = 5;
export const ROLE_COSTING_USER = 6;

// Role type definitions
export type UserRoleNumber = 1 | 2 | 3 | 4 | 5 | 6;
export type UserRoleName = 'GRC Admin' | 'Organisation Member' | 'Audit User' | 'Risk Analyst' | 'C-Suite Leadership' | 'Costing User';

// Role mapping utilities
export const RoleMap = {
  toNumber: (roleName: UserRoleName): UserRoleNumber => {
    switch (roleName) {
      case 'GRC Admin': return ROLE_GRC_ADMIN;
      case 'Organisation Member': return ROLE_ORG_MEMBER;
      case 'Audit User': return ROLE_AUDIT_USER;
      case 'Risk Analyst': return ROLE_RISK_ANALYST;
      case 'C-Suite Leadership': return ROLE_C_SUITE;
      case 'Costing User': return ROLE_COSTING_USER;
    }
  },
  toName: (roleNumber: UserRoleNumber): UserRoleName => {
    switch (roleNumber) {
      case ROLE_GRC_ADMIN: return 'GRC Admin';
      case ROLE_ORG_MEMBER: return 'Organisation Member';
      case ROLE_AUDIT_USER: return 'Audit User';
      case ROLE_RISK_ANALYST: return 'Risk Analyst';
      case ROLE_C_SUITE: return 'C-Suite Leadership';
      case ROLE_COSTING_USER: return 'Costing User';
    }
  }
};

export interface User {
  id: number;
  first_name: string;
  last_name: string;
  father_name?: string;
  login_id?: string;
  email?: string;
  dob?: string;
  gender?: string;
  phone?: string;
  pan_number?: string;
  aadhar_number?: string;
  ssn_number?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  country?: string;
  pincode?: string;
  hobbies?: Record<string, any>;
  is_active: number;
  is_approved: number;
  // Workflow fields
  workflow_state?: 'draft' | 'submitted' | 'approved' | 'rejected';
  submitted_by?: number;
  submitted_on?: string;
  approval_note?: string;
  rejected_by?: number;
  rejected_on?: string;
  rejection_reason?: string;
  approved_by?: number | null;
  approved_on?: string | null;
  entity_id?: number;
  created_by?: number;
  created_on?: string;
  modified_by?: number;
  modified_on?: string;
  // Computed / joined fields
  user_name?: string;
  user_id?: string;
  role?: UserRoleNumber;
  roleName?: UserRoleName;
  roles?: string[];
  /** Workspaces/personas the user may enter in the active entity — DERIVED
   *  server-side from the activities their roles grant (chapter-20 §20.4).
   *  Values: 'Staff' | 'GRC Team' | 'Audit' | 'Analyst' | 'Reporting' | 'Executive'.
   *  Returned by /auth/me; not a stored field. */
  workspaces?: string[];
  entity_name?: string;
  created_at?: string;
  updated_at?: string;
}

export interface UserQueryParams {
  search?: string;
  status?: 'ALL' | 'ACTIVE' | 'INACTIVE';
  page?: number;
  limit?: number;
}

export interface CreateUserRequest {
  first_name: string;
  last_name: string;
  father_name?: string;
  login_id: string;
  email: string;
  password: string;
  dob?: string;
  gender?: string;
  phone?: string;
  pan_number?: string;
  aadhar_number?: string;
  ssn_number?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  country?: string;
  pincode?: string;
  hobbies?: Record<string, any>;
}

export interface CreateQualificationRequest {
  user_id: number;
  degree_name: string;
  institution_name: string;
  period_start?: string | null;
  period_end?: string | null;
  grade_or_marks?: string | null;
  course_type?: string | null;
}

export interface CreateExperienceRequest {
  user_id: number;
  company_name: string;
  position_title: string;
  period_from?: string | null;
  period_to?: string | null;
  job_profile_desc?: string | null;
}

export interface UpdateUserRequest {
  // Optional on edit: when present, server re-hashes and updates the password.
  password?: string;
  first_name?: string;
  last_name?: string;
  father_name?: string;
  login_id?: string;
  email?: string;
  dob?: string;
  gender?: string;
  phone?: string;
  pan_number?: string;
  aadhar_number?: string;
  ssn_number?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  country?: string;
  pincode?: string;
  hobbies?: Record<string, any>;
}

export interface LoginRequest {
  login_id: string;
  password: string;
  entity_id?: number;
  module?: string;
}

export interface AuthResponse {
  success: boolean;
  message?: string;
  data?: User;
  error?: string;
}


// User CRUD operations
export const userService = {
  // Get all users with search/pagination
  async getAllUsers(params?: UserQueryParams): Promise<{ data: User[]; meta: { page: number; limit: number; total: number } }> {
    const qp = new URLSearchParams();
    if (params?.page) qp.append('page', params.page.toString());
    if (params?.limit) qp.append('limit', params.limit.toString());
    if (params?.search) qp.append('search', params.search);
    if (params?.status) qp.append('status', params.status);
    const qs = qp.toString();
    const url = `/api/v1/usr/users${qs ? `?${qs}` : ''}`;
    const response = await apiClient.get<any>(url);
    return { data: response.data.data || [], meta: response.data.meta || { page: 1, limit: 20, total: 0 } };
  },

  /** Returns all active users in the same group as the caller — for group-level pickers. */
  async getUsersByGroup(): Promise<User[]> {
    const response = await apiClient.get<any>('/api/v1/usr/users/by-group?limit=200');
    return response.data?.data || [];
  },

  /** Returns active users in caller's entity as dropdown rows {id, name, email}. */
  async getUsersDropdown(): Promise<{ id: number; name: string; email: string }[]> {
    const response = await apiClient.get<any>('/api/v1/usr/users/dropdown/all');
    return response.data || [];
  },

  // Get user by ID
  async getUserById(id: number): Promise<User> {
    const response = await apiClient.get<any>(`/api/v1/usr/users/${id}`);
    return response.data?.data || response.data;
  },

  // Create new user
  async createUser(userData: CreateUserRequest): Promise<User> {
    const response = await apiClient.post<any>('/api/v1/usr/users', userData);
    return response.data?.data || response.data;
  },

  /** Checks if a login_id is already taken (globally unique). Pass excludeId in edit mode. */
  async checkLoginIdExists(loginId: string, excludeId?: number): Promise<boolean> {
    const body: Record<string, any> = { login_id: loginId };
    if (excludeId != null) body.exclude_id = excludeId;
    const response = await apiClient.post<{ exists: boolean }>('/api/v1/usr/users/check-login-id', body);
    return !!response.data?.exists;
  },

  async createQualification(payload: CreateQualificationRequest) {
    const response = await apiClient.post<any>('/api/v1/usr/qualifications', payload);
    return response.data?.data || response.data;
  },

  async createExperience(payload: CreateExperienceRequest) {
    const response = await apiClient.post<any>('/api/v1/usr/experiences', payload);
    return response.data?.data || response.data;
  },

  // Get qualifications for a specific user
  async getQualificationsByUser(userId: number): Promise<any[]> {
    const response = await apiClient.get<any>(`/api/v1/usr/qualifications?user_id=${userId}`);
    return response.data.data || [];
  },

  // Get experiences for a specific user
  async getExperiencesByUser(userId: number): Promise<any[]> {
    const response = await apiClient.get<any>(`/api/v1/usr/experiences?user_id=${userId}`);
    return response.data.data || [];
  },

  // Delete all qualifications for a user (soft delete)
  async deleteQualificationsByUser(userId: number): Promise<void> {
    await apiClient.delete(`/api/v1/usr/qualifications/by-user/${userId}`);
  },

  // Delete all experiences for a user (soft delete)
  async deleteExperiencesByUser(userId: number): Promise<void> {
    await apiClient.delete(`/api/v1/usr/experiences/by-user/${userId}`);
  },

  // Update user
  async updateUser(id: number, userData: UpdateUserRequest): Promise<User> {
    const response = await apiClient.put<any>(`/api/v1/usr/users/${id}`, userData);
    return response.data?.data || response.data;
  },

  // Delete user (backend returns 204 No Content)
  async deleteUser(id: number): Promise<void> {
    await apiClient.delete(`/api/v1/usr/users/${id}`);
  },

  async submit(id: number): Promise<void> {
    try {
      await apiClient.post(`/api/v1/usr/users/${id}/submit`, {});
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || error.response?.data?.error || 'Failed to submit user');
    }
  },

  async approve(id: number, note?: string): Promise<void> {
    try {
      await apiClient.post(`/api/v1/usr/users/${id}/approve`, { note });
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || error.response?.data?.error || 'Failed to approve user');
    }
  },

  async reject(id: number, reason: string): Promise<void> {
    try {
      await apiClient.post(`/api/v1/usr/users/${id}/reject`, { reason });
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || error.response?.data?.error || 'Failed to reject user');
    }
  },

  async reopen(id: number): Promise<void> {
    try {
      await apiClient.post(`/api/v1/usr/users/${id}/reopen`, {});
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || error.response?.data?.error || 'Failed to reopen user');
    }
  },

  async getHistory(id: number): Promise<import('../components/common/AuditHistoryModal').AuditRecord[]> {
    const response = await apiClient.get<{ items: any[] }>(`/api/v1/usr/users/${id}/history`);
    const rows = response.data.items ?? (response.data as any) ?? [];
    return rows.map((r: any) => ({
      audit_id: r.id,
      entity_id: r.entity_id,
      action: r.event_type === 'state_transition' ? 'STATE' : 'EDIT',
      changed_by: r.changed_by,
      changed_by_username: r.changed_by_username || 'Unknown',
      changed_on: r.changed_on,
      change_reason: r.remarks,
    }));
  },
};

// Authentication operations
export const authService = {
  // Login user
  // Login user
  async login(credentials: LoginRequest): Promise<User> {
    // 1. Get Token
    const response = await apiClient.post<{ access_token: string; token_type: string }>('/api/v1/auth/login', credentials);

    if (response.data.access_token) {
      // 2. Store Token in Session Storage (Default)
      sessionStorage.setItem('anygrc_auth_token', response.data.access_token);

      // 3. Get User Profile
      const userResponse = await apiClient.get<User>('/api/v1/auth/me');

      // 4. If entity_id was passed, add it to the user object locally (since /me might not return it if it's not in DB user record yet)
      // Actually, if we put it in token, /me endpoint (if it decodes token) might return it?
      // But /me usually fetches from DB.
      // Let's manually add it to the returned user object if present in credentials
      const user = userResponse.data;
      if (credentials.entity_id) {
        user.entity_id = credentials.entity_id;
      }
      return user;
    }
    throw new Error('Login failed: No access token received');
  },

  // Logout user
  async logout(): Promise<void> {
    try {
      await apiClient.post<ApiResponse<void>>('/api/v1/auth/logout');
    } finally {
      sessionStorage.removeItem('anygrc_auth_token');
      localStorage.removeItem('anygrc_auth_token');
    }
  },

  // Get session information
  async getSession(): Promise<{ logged_in: boolean; user?: User }> {
    try {
      const response = await apiClient.get<User>('/api/v1/auth/me');
      return {
        logged_in: true,
        user: response.data
      };
    } catch (error) {
      return { logged_in: false };
    }
  },

  // Get public entities for login
  async getPublicEntities(): Promise<{ id: number; name: string }[]> {
    const response = await apiClient.get<{ id: number; name: string }[]>('/api/v1/auth/entities-public');
    return response.data;
  },
};

// Input validation helpers
export const validateUserInput = {
  // Validate user ID format
  validateUserId(userId: string): { isValid: boolean; error?: string } {
    if (!userId) {
      return { isValid: false, error: 'User ID is required' };
    }
    if (!/^[a-zA-Z0-9_]{3,20}$/.test(userId)) {
      return { isValid: false, error: 'User ID must be 3-20 characters, alphanumeric and underscore only' };
    }
    return { isValid: true };
  },

  // Validate Indian phone number
  validatePhone(phone: string): { isValid: boolean; error?: string } {
    if (!phone) {
      return { isValid: true }; // Phone is optional
    }
    const cleanPhone = phone.replace(/[\s-]/g, '');
    const patterns = [
      /^(\+91|91)?[6-9]\d{9}$/,  // With or without country code
      /^[6-9]\d{9}$/             // Without country code
    ];

    for (const pattern of patterns) {
      if (pattern.test(cleanPhone)) {
        return { isValid: true };
      }
    }
    return { isValid: false, error: 'Invalid Indian phone number format' };
  },

  // Validate email
  validateEmail(email: string): { isValid: boolean; error?: string } {
    if (!email) {
      return { isValid: true }; // Email is optional
    }
    const pattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (pattern.test(email)) {
      return { isValid: true };
    }
    return { isValid: false, error: 'Invalid email format' };
  },

  // Validate password
  validatePassword(password: string): { isValid: boolean; error?: string } {
    if (!password) {
      return { isValid: false, error: 'Password is required' };
    }
    if (password.length < 6) {
      return { isValid: false, error: 'Password must be at least 6 characters' };
    }
    if (!/[A-Za-z]/.test(password)) {
      return { isValid: false, error: 'Password must contain at least one letter' };
    }
    if (!/\d/.test(password)) {
      return { isValid: false, error: 'Password must contain at least one number' };
    }
    return { isValid: true };
  },

  // Validate user name
  validateUserName(userName: string): { isValid: boolean; error?: string } {
    if (!userName || !userName.trim()) {
      return { isValid: false, error: 'User name is required' };
    }
    if (userName.trim().length < 2) {
      return { isValid: false, error: 'User name must be at least 2 characters' };
    }
    return { isValid: true };
  },
};